import { useEffect } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { DEFAULT_PREFERENCES, resolvePreferences, type Me, type Preferences, type PreferencesPatch } from "@atrium/shared";
import { api, errorMessage } from "./api";
import { queryClient } from "./queryClient";
import { toast } from "../components/ui/Toast";
import { useAuth, useOptionalAuth } from "../features/auth/AuthProvider";

/**
 * User preferences, synced to the account (PATCH /api/me/preferences).
 * Signed-out visitors get the defaults. Reads are synchronous from the `me` query.
 */
export function usePrefs(): Preferences {
  // Shared components (avatars) also render on the developer sites, which have no main-app session.
  return useOptionalAuth()?.me?.preferences ?? DEFAULT_PREFERENCES;
}

/** Non-hook read for event handlers and plain modules. */
export function getPrefs(qc: QueryClient = queryClient): Preferences {
  return qc.getQueryData<Me | null>(["me"])?.preferences ?? DEFAULT_PREFERENCES;
}

function merge(prev: Preferences, patch: PreferencesPatch): Preferences {
  return resolvePreferences({
    ...prev,
    ...patch,
    keybinds: { ...prev.keybinds, ...(patch.keybinds ?? {}) },
    quietHours: { ...prev.quietHours, ...(patch.quietHours ?? {}) },
    customColors: { ...prev.customColors, ...(patch.customColors ?? {}) },
  });
}

function mergePatch(a: PreferencesPatch, b: PreferencesPatch): PreferencesPatch {
  return {
    ...a,
    ...b,
    ...(a.keybinds || b.keybinds ? { keybinds: { ...a.keybinds, ...b.keybinds } } : {}),
    ...(a.quietHours || b.quietHours ? { quietHours: { ...a.quietHours, ...b.quietHours } } : {}),
    ...(a.customColors || b.customColors ? { customColors: { ...a.customColors, ...b.customColors } } : {}),
  };
}

function applyLocal(qc: QueryClient, patch: PreferencesPatch) {
  const prev = qc.getQueryData<Me | null>(["me"]);
  if (prev) qc.setQueryData<Me>(["me"], { ...prev, preferences: merge(prev.preferences, patch) });
}

/*
 * Saving. Changes apply to the cache immediately. Every response from the server is
 * reconciled by replaying the changes it hasn't acknowledged yet, so a slow
 * response never undoes a newer change. On failure the account is re-read.
 */
const unacked = new Map<number, PreferencesPatch>();
let seq = 0;
let pending: PreferencesPatch | null = null;
let pendingTimer: ReturnType<typeof setTimeout> | undefined;

function reconcile(qc: QueryClient, server: Me) {
  let prefs = server.preferences;
  for (const p of unacked.values()) prefs = merge(prefs, p);
  if (pending) prefs = merge(prefs, pending);
  qc.setQueryData<Me>(["me"], { ...server, preferences: prefs });
}

async function send(qc: QueryClient, patch: PreferencesPatch) {
  const id = ++seq;
  unacked.set(id, patch);
  try {
    const updated = await api.patch<Me>("/api/me/preferences", patch);
    unacked.delete(id);
    reconcile(qc, updated);
  } catch (e) {
    unacked.delete(id);
    toast.error(errorMessage(e, "Couldn't save that setting"));
    void qc.invalidateQueries({ queryKey: ["me"] });
  }
}

function flushPending(qc: QueryClient) {
  if (!pending) return;
  clearTimeout(pendingTimer);
  const patch = pending;
  pending = null;
  void send(qc, patch);
}

/** Save now (optimistic). */
export async function savePrefs(patch: PreferencesPatch, qc: QueryClient = queryClient) {
  flushPending(qc);
  applyLocal(qc, patch);
  await send(qc, patch);
}

/** Live controls (sliders, colour pickers): apply immediately, send after `ms` without further changes. */
export function savePrefsLive(patch: PreferencesPatch, ms = 400, qc: QueryClient = queryClient) {
  applyLocal(qc, patch);
  pending = mergePatch(pending ?? {}, patch);
  clearTimeout(pendingTimer);
  pendingTimer = setTimeout(() => flushPending(qc), ms);
}

if (typeof window !== "undefined") window.addEventListener("pagehide", () => flushPending(queryClient));

/** Hook form of savePrefs, kept for existing callers. */
export function useSavePrefs() {
  const qc = useQueryClient();
  return (patch: PreferencesPatch) => savePrefs(patch, qc);
}

/* ───────────── Typography presets ───────────── */

export type TypographyPresetKey = Exclude<Preferences["typographyPreset"], "custom">;
export const TYPOGRAPHY_KEYS = ["fontFamily", "fontScale", "messageFontSize", "lineHeight", "letterSpacing", "fontWeight", "headingWeight", "usernameWeight", "density"] as const;
export type TypographyKey = (typeof TYPOGRAPHY_KEYS)[number];

export const TYPOGRAPHY_PRESETS: Record<TypographyPresetKey, { label: string } & Pick<Preferences, TypographyKey>> = {
  default: { label: "Default", fontFamily: "inter", fontScale: 1, messageFontSize: 15, lineHeight: 1.45, letterSpacing: 0, fontWeight: 400, headingWeight: 600, usernameWeight: 600, density: "comfortable" },
  compact: { label: "Compact", fontFamily: "inter", fontScale: 0.9375, messageFontSize: 14, lineHeight: 1.3, letterSpacing: 0, fontWeight: 400, headingWeight: 600, usernameWeight: 600, density: "compact" },
  readable: { label: "Readable", fontFamily: "humanist", fontScale: 1.0625, messageFontSize: 16, lineHeight: 1.65, letterSpacing: 0.01, fontWeight: 400, headingWeight: 650, usernameWeight: 650, density: "comfortable" },
  large: { label: "Large", fontFamily: "inter", fontScale: 1.1875, messageFontSize: 18, lineHeight: 1.55, letterSpacing: 0.005, fontWeight: 450, headingWeight: 700, usernameWeight: 700, density: "comfortable" },
};

/** The patch for choosing a preset. */
export function presetPatch(key: TypographyPresetKey): PreferencesPatch {
  const { label: _label, ...values } = TYPOGRAPHY_PRESETS[key];
  return { ...values, typographyPreset: key };
}

/** Editing a typography value switches to Custom unless it still matches the preset. */
export function typographyPatch(prefs: Preferences, patch: Partial<Pick<Preferences, TypographyKey>>): PreferencesPatch {
  const next = { ...prefs, ...patch };
  const match = (Object.keys(TYPOGRAPHY_PRESETS) as TypographyPresetKey[]).find((k) => TYPOGRAPHY_KEYS.every((t) => TYPOGRAPHY_PRESETS[k][t] === next[t]));
  return { ...patch, typographyPreset: match ?? "custom" };
}

/** Every appearance-related key, for "Reset to defaults". */
export const APPEARANCE_KEYS = [
  "theme",
  "accent",
  "customAccent",
  "customColors",
  "sidebarStyle",
  "messageStyle",
  "backgroundEffect",
  "backgroundColor",
  "backgroundColor2",
  "backgroundCorner",
  "backgroundStrength",
  "radius",
  "spacing",
  "borders",
  "highContrast",
  "typographyPreset",
  "codeFont",
  "codeFontSize",
  "messageDisplay",
  "roleColours",
  ...TYPOGRAPHY_KEYS,
] as const satisfies readonly (keyof Preferences)[];

export function appearanceDefaults(): PreferencesPatch {
  return Object.fromEntries(APPEARANCE_KEYS.map((k) => [k, DEFAULT_PREFERENCES[k]])) as PreferencesPatch;
}

/* ───────────── Applying to the document ───────────── */

const FONT_BASE = 16;
const SNAPSHOT_KEY = "atrium:appearance";
const THEME_KEY = "atrium:theme";

export type ResolvedTheme = "dark" | "light";
export const systemTheme = (): ResolvedTheme => (window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark");
export const resolveTheme = (t: Preferences["theme"]): ResolvedTheme => (t === "system" ? systemTheme() : t);
const systemReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Black or white text for a fill colour (WCAG relative luminance). */
export function readableOn(hex: string): string {
  const n = Number.parseInt(hex.slice(1), 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  const lum = 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
  return lum > 0.4 ? "#0c0c0b" : "#ffffff";
}

export interface Appearance {
  attrs: Record<string, string | null>;
  vars: Record<string, string | null>;
  fontSize: string;
}

const onOff = (v: boolean) => (v ? "on" : "off");

const OPPOSITE: Record<Preferences["backgroundCorner"], string> = {
  "top-left": "bottom right",
  "top-right": "bottom left",
  "bottom-left": "top right",
  "bottom-right": "top left",
};

/** The image painted by the background effect (see the rules in styles.css). */
function backgroundImage(p: Preferences): string | null {
  if (p.backgroundEffect === "off") return null;
  if (p.backgroundEffect === "gradient") return `linear-gradient(to ${OPPOSITE[p.backgroundCorner]}, ${p.backgroundColor}, ${p.backgroundColor2})`;
  // Glow from the corner, gone well before the opposite corner so it keeps the theme's base.
  return `radial-gradient(farthest-corner at ${p.backgroundCorner.replace("-", " ")}, ${p.backgroundColor}, transparent 75%)`;
}

/** Pure mapping from preferences to <html> attributes and custom properties. */
export function appearanceOf(p: Preferences): Appearance {
  const reduce = p.reduceMotion === "on" || (p.reduceMotion === "system" && systemReducedMotion());
  // A custom background without a custom text colour gets readable text automatically.
  const c = { ...p.customColors, text: p.customColors.text ?? (p.customColors.background ? (readableOn(p.customColors.background) === "#ffffff" ? "#eeeeeb" : "#121211") : null) };
  return {
    attrs: {
      theme: resolveTheme(p.theme),
      themePref: p.theme,
      accent: p.accent,
      customAccent: p.customAccent ? "" : null,
      customBg: c.background ? "" : null,
      customSurface: c.surface ? "" : null,
      customSidebar: c.sidebar ? "" : null,
      customText: c.text ? "" : null,
      sidebarStyle: p.sidebarStyle,
      messageStyle: p.messageStyle,
      borders: p.borders,
      contrast: p.highContrast ? "high" : "normal",
      font: p.fontFamily,
      codeFont: p.codeFont,
      density: p.density,
      messages: p.messageDisplay,
      underlineLinks: String(p.underlineLinks),
      motion: reduce ? "reduce" : "full",
      reactionAnimations: onOff(p.reactionAnimations && !reduce),
      particles: onOff(p.particleEffects && !reduce),
      animatedAvatars: onOff(p.animatedAvatars && !reduce),
      gifAutoplay: onOff(p.gifAutoplay && !reduce),
      videoAutoplay: onOff(p.autoplayMedia),
      timestamps: p.timestamps,
      bgEffect: p.backgroundEffect === "off" ? null : p.backgroundEffect,
    },
    vars: {
      "--user-accent": p.customAccent,
      "--user-accent-fg": p.customAccent ? readableOn(p.customAccent) : null,
      "--user-bg": c.background,
      "--user-surface": c.surface,
      "--user-sidebar": c.sidebar,
      "--user-text": c.text,
      "--radius-scale": String(p.radius),
      "--space-scale": String(p.spacing),
      "--body-weight": String(p.fontWeight),
      "--heading-weight": String(p.headingWeight),
      "--username-weight": String(p.usernameWeight),
      "--message-font-size": `${p.messageFontSize / FONT_BASE}rem`,
      "--line-height": String(p.lineHeight),
      "--letter-spacing": `${p.letterSpacing}em`,
      "--code-size": `${p.codeFontSize / FONT_BASE}rem`,
      "--code-scale": String(p.codeFontSize / 13),
      "--bg-fx-image": backgroundImage(p),
      "--bg-fx-strength": p.backgroundEffect === "off" ? null : String(p.backgroundStrength),
    },
    fontSize: `${FONT_BASE * p.fontScale}px`,
  };
}

function writeAppearance(a: Appearance) {
  const root = document.documentElement;
  for (const [k, v] of Object.entries(a.attrs)) {
    if (v === null) delete root.dataset[k];
    else root.dataset[k] = v;
  }
  for (const [k, v] of Object.entries(a.vars)) {
    if (v === null) root.style.removeProperty(k);
    else root.style.setProperty(k, v);
  }
  root.style.fontSize = a.fontSize;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", getComputedStyle(root).getPropertyValue("--bg").trim() || (a.attrs.theme === "light" ? "#ffffff" : "#0c0c0b"));
}

/** Reflect appearance preferences on <html> so CSS tokens respond, and cache them for the next load. */
export function applyPreferences(p: Preferences) {
  const a = appearanceOf(p);
  writeAppearance(a);
  try {
    localStorage.setItem(THEME_KEY, p.theme);
    localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(a));
  } catch {
    /* storage unavailable */
  }
}

/** Before the account loads: re-apply the last appearance this device saw (no flash). */
export function restoreAppearance() {
  try {
    const raw = localStorage.getItem(SNAPSHOT_KEY);
    if (raw) {
      const a = JSON.parse(raw) as Appearance;
      // The OS theme may have changed since; "system" is resolved afresh.
      if (a.attrs.themePref === "system") a.attrs.theme = systemTheme();
      writeAppearance(a);
      return;
    }
    const t = localStorage.getItem(THEME_KEY);
    if (t === "dark" || t === "light" || t === "system") document.documentElement.dataset.theme = resolveTheme(t);
  } catch {
    /* storage unavailable or corrupt */
  }
}

/** Mount once inside the app shell. Follows OS theme / motion changes when set to "system". */
export function PreferencesEffect() {
  const prefs = usePrefs();
  useEffect(() => applyPreferences(prefs), [prefs]);
  useEffect(() => {
    const queries = [window.matchMedia("(prefers-color-scheme: light)"), window.matchMedia("(prefers-reduced-motion: reduce)")];
    const onChange = () => applyPreferences(getPrefs());
    queries.forEach((q) => q.addEventListener("change", onChange));
    return () => queries.forEach((q) => q.removeEventListener("change", onChange));
  }, []);
  return null;
}

/* ───────────── Small helpers for other modules ───────────── */

/** Theme switching (synced). */
export function setTheme(theme: Preferences["theme"]) {
  return savePrefs({ theme });
}
export function toggleTheme() {
  const p = getPrefs();
  return setTheme(resolveTheme(p.theme) === "dark" ? "light" : "dark");
}

/** Whether animated media may play (animated avatars, GIFs, videos), honouring reduce motion. */
export function useMotionPrefs() {
  const p = usePrefs();
  const reduce = p.reduceMotion === "on" || (p.reduceMotion === "system" && systemReducedMotion());
  return {
    reduce,
    animatedAvatars: p.animatedAvatars && !reduce,
    gifAutoplay: p.gifAutoplay && !reduce,
    videoAutoplay: p.autoplayMedia,
    reactionAnimations: p.reactionAnimations && !reduce,
    particleEffects: p.particleEffects && !reduce,
  };
}

/** Pick the still frame of an avatar/emoji when animation is off. */
export function useAnimatedSrc(src: string | null | undefined, staticSrc: string | null | undefined): string | null | undefined {
  const { animatedAvatars } = useMotionPrefs();
  return !animatedAvatars && staticSrc ? staticSrc : src;
}
